import { useEffect, useRef } from 'react'
import type { FieldValues, UseFormClearErrors, UseFormSetError } from 'react-hook-form'
import { pushFormErrorHandler } from '../utils/formErrors'

/**
 * Liga um formulário react-hook-form ao intercetor global de erros: quando o
 * backend recusa o pedido por validação (HTTP 422), as mensagens de cada campo
 * obrigatório aparecem por baixo do respectivo input — em vez de um toast
 * genérico a dizer que não foi possível gravar.
 *
 * Usar logo a seguir a `useForm`, passando `setError` e `clearErrors`:
 *
 * ```ts
 * const { register, handleSubmit, setError, clearErrors, formState } = useForm(...)
 * useServerFieldErrors(setError, clearErrors)
 * ```
 *
 * `fieldMap` traduz nomes de campo do backend para nomes do formulário quando
 * diferem (ex: `{ 'endereco.rua': 'rua' }`). Deve ser estável (constante fora do
 * componente ou memoizado).
 */
export function useServerFieldErrors<T extends FieldValues>(
    setError: UseFormSetError<T>,
    clearErrors: UseFormClearErrors<T>,
    fieldMap?: Record<string, string>,
) {
    // As funções do react-hook-form são estáveis entre renders, mas guardamo-las
    // num ref para não depender disso e evitar re-registar o handler.
    const fns = useRef({ setError, clearErrors })
    fns.current = { setError, clearErrors }

    useEffect(() => {
        return pushFormErrorHandler({
            setError: (name, err, opts) =>
                fns.current.setError(name as Parameters<UseFormSetError<T>>[0], err, opts),
            clearErrors: (name) =>
                fns.current.clearErrors(name as Parameters<UseFormClearErrors<T>>[0]),
            fieldMap,
        })
        // fieldMap deve ser estável; mudanças são ignoradas de propósito.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
}
